
import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Participant } from "@/hooks/useParticipants";
import FilterBar from "@/components/FilterBar";

interface ParticipantListProps {
  participants: Participant[];
  showPremiumRows?: boolean;
  metricType?: string;
  displayShowAll?: boolean; // New prop for showing the "View all" link
  title?: string; // Optional title prop
}

const ParticipantList: React.FC<ParticipantListProps> = ({
  participants,
  showPremiumRows = true,
  metricType,
  displayShowAll = false, // Default to false
  title = "Participants Overview"
}) => {
  const [filteredParticipants, setFilteredParticipants] = useState(participants);
  
  // Update filtered participants when participants prop changes
  useEffect(() => {
    setFilteredParticipants(participants);
  }, [participants]);
  
  // Mock credit data (in a real app this would come from props or context)
  const totalCredits = 1000;
  const usedCredits = 600;
  const percentUsed = Math.floor((usedCredits / totalCredits) * 100);
  const navigate = useNavigate();
  const location = useLocation();

  const handleFiltersChange = (filters: string[]) => {
    if (filters.length === 0) {
      setFilteredParticipants(participants);
      return;
    }

    const filtered = participants.filter(participant => {
      return filters.every(filter => {
        // Check age group
        if (participant.ageGroup === filter) return true;
        // Check BMI group
        if (participant.bmiGroup === filter) return true;
        // Check tags
        if (participant.tags.includes(filter)) return true;
        return false;
      });
    });
    
    setFilteredParticipants(filtered);
  };

  const handleRowClick = (userId: string) => {
    navigate(`/dashboard/participants/${userId}`, { state: { from: location.pathname, fromLabel: location.pathname === '/dashboard' ? 'dashboard' : location.pathname.startsWith('/dashboard/tastermonials') ? 'tastermonials' : location.pathname.startsWith('/dashboard/experiments') ? 'experiment details' : 'previous page' } });
  };

  const getTagStyle = (tag: string) => {
    if (tag === 'Diabetic') {
      return "bg-red-100 text-red-800 border-red-300";
    }
    return "bg-gray-100";
  };

  return (
    <Card>
      {/* Updated header to conditionally show the "View all" link */}
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>Study participant demographic information</CardDescription>
        </div>
        {displayShowAll && (
          <Button variant="link" asChild className="px-2 ml-auto">
            <Link to="/dashboard/participants">View all participants</Link>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <FilterBar 
            participants={participants} 
            onFiltersChange={handleFiltersChange}
          />
        </div>
        {filteredParticipants.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            No participants found.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Age Group</TableHead>
                <TableHead>Gender</TableHead>
                <TableHead>Ethnicity</TableHead>
                <TableHead>BMI Group</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Tier</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredParticipants.map((participant) => (
                <TableRow
                  key={participant.userExperimentId}
                  onClick={() => handleRowClick(participant.userId)}
                  className="cursor-pointer hover:bg-muted"
                >
                  <TableCell>{participant.ageGroup}</TableCell>
                  <TableCell>{participant.gender}</TableCell>
                  <TableCell>{participant.ethnicity}</TableCell>
                  <TableCell>{participant.bmiGroup}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(() => {
                        let charCount = 0;
                        let tagsToShow = 0;
                        
                        for (let i = 0; i < participant.tags.length; i++) {
                          const tagLength = participant.tags[i].length;
                          if (charCount + tagLength <= 25) {
                            charCount += tagLength;
                            tagsToShow++;
                          } else {
                            break;
                          }
                        }
                        
                        return (
                          <>
                            {participant.tags.slice(0, tagsToShow).map((tag, index) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className={getTagStyle(tag)}
                              >
                                {tag}
                              </Badge>
                            ))}
                            {participant.tags.length > tagsToShow && (
                              <Badge variant="outline" className="bg-gray-100">
                                +{participant.tags.length - tagsToShow} more
                              </Badge>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      participant.tier === "Influencer" ? "bg-indigo-100 text-indigo-800" :
                      participant.tier === "Reviewer" ? "bg-green-100 text-green-800" :
                      "bg-gray-100 text-gray-800"
                    }>
                      {participant.tier}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}

              {/* Blurred premium rows */}
              {showPremiumRows && filteredParticipants.length > 2 && filteredParticipants.slice(2).map((participant) => (
                <TableRow key={participant.userExperimentId} className="filter blur-sm opacity-50">
                  <TableCell>{participant.ageGroup}</TableCell>
                  <TableCell>{participant.gender}</TableCell>
                  <TableCell>{participant.ethnicity}</TableCell>
                  <TableCell>{participant.bmiGroup}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {participant.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="bg-gray-100">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-gray-100">
                      {participant.tier}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Premium Feature Banner */}
        {showPremiumRows && filteredParticipants.length > 2 && (
          <div className="mt-6 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-4 text-white">
            <h3 className="text-lg font-bold mb-1">Use 400 credits to gain access</h3>
            <p className="mb-3">View the next ten available participant data points and unlock advanced analytics.</p>

            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Credits: {usedCredits} / {totalCredits} used</span>
                <span>{totalCredits - usedCredits} available</span>
              </div>
              <Progress className="h-2 bg-white/30" value={percentUsed}>
                <div className="bg-white" />
              </Progress>
            </div>

            <div className="flex gap-3">
              <Button className="bg-white text-indigo-700 hover:bg-gray-100">
                Unlock participant data
              </Button>
              <Button variant="outline" className="border-white text-white bg-transparent hover:bg-white hover:text-indigo-700">
                Show usage
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ParticipantList;
